import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSimulationScores from '../../hooks/useSimulationScores';
import { AuthContext } from '../AuthContext';
import { useToast } from '../Toast';
import TeamDataPanel from './TeamDataPanel';
import {
    getAllTeamsInGame, updateLockedRounds, updateReleasedRounds, subscribeToGame,
    updateGameName, updateGameStatus, deleteGame,
    renameTeam, deleteTeam, unsubmitRound,
} from '../../firebase/db';

const SimulationDetail = () => {
    const navigate = useNavigate();
    const { gameId } = useParams();
    const { currentUser } = useContext(AuthContext);
    const { teams, loading, error } = useSimulationScores('Master', gameId);
    const { showToast, ToastContainer } = useToast();

    const [allTeams, setAllTeams] = useState([]);
    const [rosterOpen, setRosterOpen] = useState(false);
    const [lockedRounds, setLockedRounds] = useState([]);
    const [releasedRounds, setReleasedRounds] = useState([]);

    // Game metadata
    const [gameName, setGameName] = useState('');
    const [gameCode, setGameCode] = useState('');
    const [gameStatus, setGameStatus] = useState('active');

    // Inline editing state
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [editingTeam, setEditingTeam] = useState(null);
    const [teamNameInput, setTeamNameInput] = useState('');
    const [expandedTeam, setExpandedTeam] = useState(null);

    const refreshRoster = useCallback(() => {
        getAllTeamsInGame('Master', gameId)
            .then(setAllTeams)
            .catch(() => {});
    }, [gameId]);

    useEffect(() => {
        refreshRoster();
    }, [refreshRoster]);

    // Subscribe to game document for locked rounds + game metadata
    useEffect(() => {
        const unsubscribe = subscribeToGame(gameId, (game) => {
            setLockedRounds(game.lockedRounds || []);
            setReleasedRounds(game.releasedRounds || []);
            setGameName(game.name || '');
            setGameCode(game.gameCode || '');
            setGameStatus(game.status || 'active');
        });
        return () => unsubscribe();
    }, [gameId]);

    // Use allTeams for roster (includes non-submitted), fall back to scoring teams
    const rosterTeams = allTeams.length > 0 ? allTeams : teams;

    // Determine which rounds are complete (all teams submitted for that round)
    const getRoundStatus = (roundIndex) => {
        if (rosterTeams.length === 0) return { complete: false, submitted: 0, total: 0 };
        const submitted = rosterTeams.filter(t =>
            t.teamData.rounds?.[roundIndex]?.is_submitted === true
        ).length;
        return {
            complete: submitted === rosterTeams.length && rosterTeams.length > 0,
            submitted,
            total: rosterTeams.length,
        };
    };

    const rounds = [0, 1, 2].map(i => ({
        number: i + 1,
        ...getRoundStatus(i),
    }));

    const handleToggleLock = async (roundNumber) => {
        const newLocked = lockedRounds.includes(roundNumber)
            ? lockedRounds.filter(r => r !== roundNumber)
            : [...lockedRounds, roundNumber];
        try {
            await updateLockedRounds(gameId, newLocked);
        } catch (err) {
            console.error('Error updating locked rounds:', err);
        }
    };

    const handleToggleRelease = async (roundNumber) => {
        const newReleased = releasedRounds.includes(roundNumber)
            ? releasedRounds.filter(r => r !== roundNumber)
            : [...releasedRounds, roundNumber];
        try {
            await updateReleasedRounds(gameId, newReleased);
            const action = newReleased.includes(roundNumber) ? 'released' : 'hidden';
            showToast(`Round ${roundNumber} results ${action} for students.`, 'success');
        } catch (err) {
            showToast('Error updating released rounds.', 'error');
        }
    };

    // --- Game management handlers ---

    const handleSaveName = async () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        try {
            await updateGameName(gameId, trimmed);
            setEditingName(false);
            showToast('Game name updated.', 'success');
        } catch (err) {
            showToast('Error updating game name.', 'error');
        }
    };

    const handleToggleStatus = async () => {
        const newStatus = gameStatus === 'active' ? 'completed' : 'active';
        const msg = newStatus === 'completed'
            ? 'Mark this game as completed? It will move to the Completed Games section.'
            : 'Reactivate this game? It will move back to Active Games.';
        if (!window.confirm(msg)) return;
        try {
            await updateGameStatus(gameId, newStatus);
            showToast(`Game marked as ${newStatus}.`, 'success');
        } catch (err) {
            showToast('Error updating game status.', 'error');
        }
    };

    const handleDeleteGame = async () => {
        if (!window.confirm('Are you sure you want to delete this game? This cannot be undone. Teams will NOT be deleted.')) return;
        try {
            await deleteGame(gameId);
            showToast('Game deleted.', 'success');
            navigate('/professor-dashboard');
        } catch (err) {
            showToast('Error deleting game.', 'error');
        }
    };

    const copyLink = () => {
        const link = `${window.location.origin}/?game=${gameCode}`;
        navigator.clipboard.writeText(link);
        showToast('Student link copied to clipboard!', 'success');
    };

    // --- Team management handlers ---

    const handleRenameTeam = async (oldName) => {
        const newName = teamNameInput.trim();
        if (!newName) {
            showToast('Team name cannot be empty.', 'error');
            return;
        }
        if (newName.includes(' ')) {
            showToast('Team name cannot contain spaces.', 'error');
            return;
        }
        if (newName === oldName) {
            setEditingTeam(null);
            return;
        }
        if (!window.confirm(`Rename team from "${oldName}" to "${newName}"? Students using the old name will need the new URL.`)) return;
        try {
            await renameTeam(oldName, newName, 'Master');
            showToast(`Team renamed from "${oldName}" to "${newName}".`, 'success');
            setEditingTeam(null);
            if (expandedTeam === oldName) setExpandedTeam(newName);
            refreshRoster();
        } catch (err) {
            showToast(err.message || 'Error renaming team.', 'error');
        }
    };

    const handleDeleteTeam = async (teamName) => {
        if (!window.confirm(`Delete team "${teamName}"? This permanently removes all submission data.`)) return;
        try {
            await deleteTeam(teamName, 'Master');
            showToast(`Team "${teamName}" deleted.`, 'success');
            if (expandedTeam === teamName) setExpandedTeam(null);
            refreshRoster();
        } catch (err) {
            showToast('Error deleting team.', 'error');
        }
    };

    const handleUnsubmitRound = async (teamName, roundIndex) => {
        const roundNum = roundIndex + 1;
        if (!window.confirm(`Re-open Round ${roundNum} for "${teamName}"? The student will be able to edit and resubmit.`)) return;
        try {
            await unsubmitRound(teamName, 'Master', roundIndex);
            showToast(`Round ${roundNum} re-opened for "${teamName}".`, 'success');
            refreshRoster();
        } catch (err) {
            showToast('Error re-opening round.', 'error');
        }
    };

    if (loading) {
        return (
            <div style={{
                maxWidth: '800px', margin: '40px auto', padding: '20px',
                textAlign: 'center', fontSize: '18px', color: '#666',
            }}>
                Loading simulation data...
            </div>
        );
    }

    return (
        <div style={{
            maxWidth: '800px', margin: '40px auto', padding: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '10px',
            fontFamily: 'Arial, sans-serif', backgroundColor: '#fafafa',
            position: 'relative',
        }}>
            {ToastContainer}
            <button
                onClick={() => navigate('/professor-dashboard')}
                style={{
                    position: 'absolute', top: '20px', left: '20px',
                    padding: '8px 16px', backgroundColor: '#1976d2', color: '#fff',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', zIndex: 1000,
                }}
            >
                Back
            </button>

            {/* Game Header */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                {editingName ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                        <input
                            type="text"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                            autoFocus
                            style={{
                                fontSize: '20px', fontWeight: 'bold', padding: '6px 12px',
                                border: '2px solid #4b2e83', borderRadius: '6px', textAlign: 'center',
                                width: '300px',
                            }}
                        />
                        <button
                            onClick={handleSaveName}
                            style={{
                                padding: '6px 14px', backgroundColor: '#4b2e83', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold',
                            }}
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setEditingName(false)}
                            style={{
                                padding: '6px 14px', backgroundColor: '#ccc', color: '#333',
                                border: 'none', borderRadius: '4px', cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <h1 style={{ color: '#333', marginBottom: '4px' }}>
                        {gameName || 'Untitled Game'}
                        <button
                            onClick={() => { setNameInput(gameName); setEditingName(true); }}
                            style={{
                                marginLeft: '10px', padding: '4px 10px', fontSize: '12px',
                                backgroundColor: 'transparent', color: '#1976d2', border: '1px solid #1976d2',
                                borderRadius: '4px', cursor: 'pointer', verticalAlign: 'middle',
                            }}
                        >
                            Edit
                        </button>
                    </h1>
                )}

                {/* Game code + copy link */}
                {gameCode && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px', color: '#666' }}>
                            Code: <strong style={{ color: '#333', letterSpacing: '1px' }}>{gameCode}</strong>
                        </span>
                        <button
                            onClick={copyLink}
                            style={{
                                padding: '4px 10px', fontSize: '12px', backgroundColor: '#4b2e83', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer',
                            }}
                        >
                            Copy Link
                        </button>
                    </div>
                )}

                {/* Status badge + toggle */}
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: gameStatus === 'active' ? '#e8f5e9' : '#fff3e0',
                        color: gameStatus === 'active' ? '#2e7d32' : '#e65100',
                        border: `1px solid ${gameStatus === 'active' ? '#a5d6a7' : '#ffb74d'}`,
                    }}>
                        {gameStatus === 'active' ? 'Active' : 'Completed'}
                    </span>
                    <button
                        onClick={handleToggleStatus}
                        style={{
                            padding: '4px 10px', fontSize: '12px',
                            backgroundColor: gameStatus === 'active' ? '#ff9800' : '#2e7d32',
                            color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer',
                        }}
                    >
                        {gameStatus === 'active' ? 'Mark Complete' : 'Reactivate'}
                    </button>
                </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
                <p><strong>Professor:</strong> {currentUser?.displayName || currentUser?.email || 'Instructor'}</p>
                <p><strong>Teams:</strong> {rosterTeams.length}</p>

                {error && (
                    <p style={{ color: '#e15759' }}>Error loading data: {error.message}</p>
                )}

                {rounds.map((round) => {
                    const isLocked = lockedRounds.includes(round.number);
                    const isReleased = releasedRounds.includes(round.number);
                    return (
                        <div
                            key={round.number}
                            style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '12px', marginBottom: '20px',
                                backgroundColor: isLocked ? '#fff3e0' : '#fff',
                                border: `1px solid ${isLocked ? '#ffb74d' : '#ddd'}`,
                                borderRadius: '8px',
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <strong>Round {round.number}:</strong>{' '}
                                {round.complete
                                    ? 'Complete \u2705'
                                    : `${round.submitted}/${round.total} submitted`}
                                {isLocked && (
                                    <span style={{
                                        marginLeft: '8px', fontSize: '12px', color: '#e65100',
                                        fontWeight: 'bold',
                                    }}>
                                        LOCKED
                                    </span>
                                )}
                                {isReleased && (
                                    <span style={{
                                        marginLeft: '8px', fontSize: '12px', color: '#2e7d32',
                                        fontWeight: 'bold',
                                    }}>
                                        RESULTS RELEASED
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                    onClick={() => handleToggleLock(round.number)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: isLocked ? '#ff9800' : '#e0e0e0',
                                        color: isLocked ? '#fff' : '#333',
                                        border: 'none', borderRadius: '4px', cursor: 'pointer',
                                        fontSize: '12px', fontWeight: 'bold', width: '80px',
                                    }}
                                >
                                    {isLocked ? 'Unlock' : 'Lock'}
                                </button>
                                <button
                                    onClick={() => handleToggleRelease(round.number)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: isReleased ? '#2e7d32' : 'transparent',
                                        color: isReleased ? '#fff' : '#2e7d32',
                                        border: isReleased ? 'none' : '1px solid #2e7d32',
                                        borderRadius: '4px', cursor: 'pointer',
                                        fontSize: '12px', fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {isReleased ? 'Hide Results' : 'Release Results'}
                                </button>
                                {round.submitted > 0 ? (
                                    <button
                                        style={{
                                            padding: '8px 12px', backgroundColor: '#1976d2', color: '#fff',
                                            border: 'none', borderRadius: '5px',
                                            width: '120px', cursor: 'pointer', fontWeight: 'bold',
                                        }}
                                        onClick={() => navigate(`/simulation/${gameId}/scores/${round.number}`)}
                                    >
                                        View Scores
                                    </button>
                                ) : (
                                    <button
                                        style={{
                                            padding: '8px 12px', backgroundColor: '#cce5ff', color: '#666',
                                            border: 'none', borderRadius: '5px',
                                            width: '120px', cursor: 'not-allowed',
                                        }}
                                        disabled
                                    >
                                        View Scores
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Team Roster */}
                <div
                    style={{
                        cursor: 'pointer', userSelect: 'none', display: 'flex',
                        alignItems: 'center', gap: '8px', marginTop: '10px', marginBottom: '10px',
                    }}
                    onClick={() => setRosterOpen(!rosterOpen)}
                >
                    <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#4b2e83' }}>
                        Team Roster {rosterOpen ? '\u25B2' : '\u25BC'}
                    </span>
                    <span style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                        {rosterTeams.length} team{rosterTeams.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {rosterOpen && (
                    <div style={{
                        backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px',
                        overflow: 'hidden',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr>
                                    <th style={{
                                        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 12px',
                                        textAlign: 'left', borderBottom: '2px solid #ddd',
                                    }}>
                                        Team Name
                                    </th>
                                    {[1, 2, 3].map(r => (
                                        <th key={r} style={{
                                            backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
                                            textAlign: 'center', borderBottom: '2px solid #ddd', width: '70px',
                                        }}>
                                            Rd {r}
                                        </th>
                                    ))}
                                    <th style={{
                                        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
                                        textAlign: 'center', borderBottom: '2px solid #ddd', width: '120px',
                                    }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...rosterTeams].sort((a, b) => a.teamName.localeCompare(b.teamName)).map(team => {
                                    const isEditing = editingTeam === team.teamName;
                                    const isExpanded = expandedTeam === team.teamName;
                                    return (
                                        <React.Fragment key={team.teamName}>
                                            <tr>
                                                {/* Team name cell */}
                                                <td style={{
                                                    padding: '8px 12px', borderBottom: '1px solid #eee',
                                                    fontWeight: 'bold',
                                                }}>
                                                    {isEditing ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <input
                                                                type="text"
                                                                value={teamNameInput}
                                                                onChange={(e) => setTeamNameInput(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleRenameTeam(team.teamName);
                                                                    if (e.key === 'Escape') setEditingTeam(null);
                                                                }}
                                                                autoFocus
                                                                style={{
                                                                    padding: '4px 6px', fontSize: '13px',
                                                                    border: '1px solid #4b2e83', borderRadius: '3px',
                                                                    width: '140px',
                                                                }}
                                                            />
                                                            <button
                                                                onClick={() => handleRenameTeam(team.teamName)}
                                                                style={{
                                                                    padding: '3px 8px', fontSize: '11px',
                                                                    backgroundColor: '#4b2e83', color: '#fff',
                                                                    border: 'none', borderRadius: '3px', cursor: 'pointer',
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingTeam(null)}
                                                                style={{
                                                                    padding: '3px 8px', fontSize: '11px',
                                                                    backgroundColor: '#ccc', color: '#333',
                                                                    border: 'none', borderRadius: '3px', cursor: 'pointer',
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => setExpandedTeam(isExpanded ? null : team.teamName)}
                                                            title="Click to expand/collapse team details"
                                                        >
                                                            {isExpanded ? '\u25BC' : '\u25B6'}{' '}
                                                            {team.teamName}
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Round submission cells - clickable to unsubmit */}
                                                {[0, 1, 2].map(i => {
                                                    const submitted = team.teamData.rounds?.[i]?.is_submitted === true;
                                                    return (
                                                        <td key={i} style={{
                                                            padding: '8px', textAlign: 'center',
                                                            borderBottom: '1px solid #eee',
                                                        }}>
                                                            {submitted ? (
                                                                <button
                                                                    onClick={() => handleUnsubmitRound(team.teamName, i)}
                                                                    title={`Click to re-open Round ${i + 1}`}
                                                                    style={{
                                                                        background: 'none', border: 'none',
                                                                        cursor: 'pointer', fontSize: '14px',
                                                                        color: '#2e7d32', padding: '2px',
                                                                    }}
                                                                    onMouseEnter={(e) => { e.target.style.color = '#e65100'; }}
                                                                    onMouseLeave={(e) => { e.target.style.color = '#2e7d32'; }}
                                                                >
                                                                    {'\u2705'}
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: '#999' }}>{'\u2014'}</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}

                                                {/* Actions cell */}
                                                <td style={{
                                                    padding: '8px', textAlign: 'center',
                                                    borderBottom: '1px solid #eee',
                                                }}>
                                                    <button
                                                        onClick={() => {
                                                            setEditingTeam(team.teamName);
                                                            setTeamNameInput(team.teamName);
                                                        }}
                                                        title="Rename team"
                                                        style={{
                                                            background: 'none', border: 'none', cursor: 'pointer',
                                                            fontSize: '14px', padding: '2px 4px', color: '#1976d2',
                                                        }}
                                                    >
                                                        {'\u270F\uFE0F'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteTeam(team.teamName)}
                                                        title="Delete team"
                                                        style={{
                                                            background: 'none', border: 'none', cursor: 'pointer',
                                                            fontSize: '14px', padding: '2px 4px', color: '#d32f2f',
                                                            marginLeft: '4px',
                                                        }}
                                                    >
                                                        {'\uD83D\uDDD1\uFE0F'}
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expanded team data panel */}
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={5} style={{
                                                        padding: '0', borderBottom: '2px solid #4b2e83',
                                                        backgroundColor: '#f9f7fc',
                                                    }}>
                                                        <TeamDataPanel
                                                            teamName={team.teamName}
                                                            teamData={team.teamData}
                                                            onUpdate={refreshRoster}
                                                            showToast={showToast}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Delete Game */}
                <div style={{ marginTop: '30px', textAlign: 'center', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <button
                        onClick={handleDeleteGame}
                        style={{
                            padding: '8px 20px', backgroundColor: 'transparent', color: '#d32f2f',
                            border: '1px solid #d32f2f', borderRadius: '4px', cursor: 'pointer',
                            fontSize: '13px',
                        }}
                    >
                        Delete Game
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SimulationDetail;
